/**
 * Shared feature naming functions for Decision Engine feature matrix.
 *
 * Used by both backend (feature-matrix-relationships.ts, feature-matrix.ts) and
 * frontend (feature-matrices/utils.ts) to ensure feature names are always consistent.
 *
 * This module has NO backend-specific dependencies (no zapatos, no Source, etc.)
 * so it can be safely imported from frontend code via @hightouch/lib.
 */

/**
 * Generates the output column name for an aggregation with a time window.
 *
 * @param outputName - The base output name prefix
 * @param timeWindowDays - Optional time window in days
 * @returns Column name like "purchase_count_7d" or "purchase_count" if no time window
 */
export function getAggregationOutputColumnName(
  outputName: string,
  timeWindowDays: number,
): string {
  if (!timeWindowDays || timeWindowDays === -1) {
    return outputName;
  }
  return `${outputName}_${timeWindowDays}d`;
}

/**
 * Builds the base output name for a column aggregation, before the time-window suffix.
 * Single source of truth for the naming pattern used by both the SQL query builder
 * (generateColumnAggregations) and the insights feature list (getInsightsFeaturesFromMatrix).
 *
 * @returns Names like "amount_sum" or "amount_sum_a3f2c1d4" (with filter hash)
 */
export function getAggBaseName(
  columnName: string,
  aggregationType: string,
  filterId?: string,
): string {
  const base = `${columnName}_${aggregationType.toLowerCase()}`;
  return filterId !== undefined ? `${base}_${filterId}` : base;
}

export function getEffectiveTimeWindows(
  timeWindowDays?: number[] | null,
): number[] {
  return timeWindowDays && timeWindowDays.length > 0 ? timeWindowDays : [-1];
}

export interface ColumnVariant {
  aggregationType: string;
  timeWindow: number;
  filterId?: string;
  /** Final output column name (e.g., "amount_sum_7d", "amount_sum_a3f2c1d4_30d") */
  outputName: string;
}

/**
 * Builds a human-readable display name for an Insights breakdown dimension.
 *
 * Parent model columns → "Column Name"
 * Related model columns → "Model Name > Column Name (Aggregation, 30d)"
 * With filter           → "Model Name > Column Name (Sum, where Category = electronics)"
 * Universal count       → "Model Name > Count (30d)"
 */
export function computeInsightsDisplayName({
  modelName,
  sourceColumn,
  isParentModel,
  aggregationType,
  timeWindowDays,
  filterDescription,
}: {
  modelName: string;
  sourceColumn: string;
  isParentModel: boolean;
  aggregationType?: string;
  timeWindowDays?: number;
  filterDescription?: string;
}): string {
  const columnLabel = titleCase(sourceColumn);

  if (isParentModel) {
    return columnLabel;
  }

  const qualifiers: string[] = [];
  if (aggregationType) {
    qualifiers.push(titleCase(aggregationType));
  }
  if (timeWindowDays && timeWindowDays !== -1) {
    qualifiers.push(`${timeWindowDays}d`);
  }
  if (filterDescription) {
    qualifiers.push(`where ${filterDescription}`);
  }

  const suffix = qualifiers.length > 0 ? ` (${qualifiers.join(", ")})` : "";
  return `${modelName} > ${columnLabel}${suffix}`;
}

function titleCase(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export interface SummarizableCondition {
  type?: string;
  property?: unknown;
  operator?: string;
  value?: unknown;
  conditions?: SummarizableCondition[];
}

/**
 * Summarizes a filter condition into a short human-readable string.
 * e.g. "Category = electronics" or "Category = electronics and Amount > 75"
 */
export function summarizeFilterCondition(
  condition: SummarizableCondition,
): string {
  if (isConditionGroup(condition)) {
    const joiner = condition.type === "or" ? " or " : " and ";
    return condition.conditions
      .map((child) =>
        isConditionGroup(child)
          ? `(${summarizeFilterCondition(child)})`
          : summarizeFilterCondition(child),
      )
      .join(joiner);
  }
  if (
    (condition.type === "property" || condition.type === undefined) &&
    typeof condition.operator === "string"
  ) {
    return formatSingleCondition(
      condition.property,
      condition.operator,
      condition.value,
    );
  }
  return condition.type ?? "";
}

function isConditionGroup(
  condition: SummarizableCondition,
): condition is SummarizableCondition & {
  conditions: SummarizableCondition[];
} {
  return (
    (condition.type === "and" || condition.type === "or") &&
    Array.isArray(condition.conditions)
  );
}

function formatSingleCondition(
  property: unknown,
  operator: string,
  value: unknown,
): string {
  const propName =
    typeof property === "string"
      ? titleCase(property)
      : typeof property === "object" && property !== null && "name" in property
        ? titleCase(String((property as { name: string }).name))
        : "?";
  const displayValue =
    typeof value === "object" ? JSON.stringify(value) : String(value);
  return `${propName} ${operator} ${displayValue}`;
}

/**
 * Canonical enumeration of aggregation × time window × filter combinations
 * for a column. All code paths that need to know "what variants does this
 * column produce?" must use this function to stay in sync.
 *
 * Callers pre-compute filter IDs before calling (backend uses sync getFilterId,
 * frontend uses async getFilterIdAsync). An `undefined` filter ID is the unfiltered
 * variant; `[]` and `[undefined]` are equivalent.
 */
export function iterateColumnVariants(
  columnName: string,
  aggregations: string[],
  filterIds: (string | undefined)[],
  timeWindowDays?: number[] | null,
): ColumnVariant[] {
  const effectiveTimeWindows = getEffectiveTimeWindows(timeWindowDays);
  const effectiveFilterIds = filterIds.length === 0 ? [undefined] : filterIds;
  const variants: ColumnVariant[] = [];

  for (const filterId of effectiveFilterIds) {
    for (const aggregationType of aggregations) {
      for (const timeWindow of effectiveTimeWindows) {
        variants.push({
          aggregationType,
          timeWindow,
          ...(filterId !== undefined ? { filterId } : {}),
          outputName: getAggregationOutputColumnName(
            getAggBaseName(columnName, aggregationType, filterId),
            timeWindow,
          ),
        });
      }
    }
  }

  return variants;
}
